import React, { useState, useEffect } from 'react';
import '../styles.css';
import Header from '../Components/Header';
import TopHeader from '../Components/Header/topHeader';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField
} from '@mui/material';
import { useSelector } from "react-redux";
import { AiOutlineEye } from 'react-icons/ai';
import apiConfig from '../../config/api.js';

// Helper function to convert month number to month name
const getMonthName = (monthNumber) => {
  const date = new Date();
  date.setMonth(monthNumber - 1); // Month number is 1-based, while JavaScript Date months are 0-based.
  return date.toLocaleString('en-US', { month: 'long' });
};

// Helper function to format date
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const Payments = () => {
  const [userId, setUserId] = useState(''); // Store user ID
  const isLang = useSelector((state) => state.authReducer.lang);
  const developeruid = useSelector((state) => state.authReducer.developeruid);
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [filter, setFilter] = useState("");
  const [totalBalance, setTotalBalance] = useState(0); // To store the total balance
  const [openInvoice, setOpenInvoice] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [firstPaymentDate, setFirstPaymentDate] = useState(null); // Store first payment date
  const [lastPaymentDate, setLastPaymentDate] = useState(null); // Store last payment date
  const [templates, setTemplates] = useState([]); // Store fetched templates for the invoice
  const [username, setUsername] = useState('');
  const [fullname, setFullname] = useState('');
  const [role, setRole] = useState('');
  const [bankName, setBankName] = useState(''); // New bank_name state
  const [bankNo, setBankNo] = useState(''); // New bank_no state
  const [accountTitle, setAccountTitle] = useState(''); // New account_title state
  const [balance, setBalance] = useState(''); // New balance state

  // Fetch payments data
  const fetchPayments = async () => {
    const formData = new FormData();
    formData.append("userid", developeruid);
    const response = await fetch(apiConfig.getUrl(apiConfig.ENDPOINTS.GET_PAYMENTS), {
      method: "POST",
      body: formData,
    });
    const data = await response.json();
    setPayments(data);
    setFilteredPayments(data);

    // Calculate total balance by summing up all `total_amount` fields
    const total = data.reduce((sum, payment) => sum + parseFloat(payment.total_amount || 0), 0);
    setTotalBalance(total);

    // Sort payments by the `paid_at` date and get first and last payment dates
    if (data.length > 0) {
      const sortedPayments = [...data].sort((a, b) => new Date(a.paid_at) - new Date(b.paid_at));
      setFirstPaymentDate(sortedPayments[0].paid_at);
      setLastPaymentDate(sortedPayments[sortedPayments.length - 1].paid_at);
    }
  };

  useEffect(() => {
    if (developeruid) {
      fetchPayments();
    }
  }, [developeruid]);

  // Fetch templates based on developeruid and month
  const fetchTemplates = async (month) => {
    const formData = new FormData();
    formData.append("id", developeruid);
    formData.append("month", month);

    const response = await fetch(apiConfig.getUrl(apiConfig.ENDPOINTS.GET_RECEIPT_TEMPLATES), {
      method: "POST",
      body: formData,
    });
    const data = await response.json();
    setTemplates(data); // Store the fetched templates
  };

  // Filter payments based on user input
  const filterPayments = (filterValue) => {
    setFilter(filterValue);
    const filtered = payments.filter(payment =>
      getMonthName(payment.month).toLowerCase().includes(filterValue.toLowerCase()) ||
      payment.total_amount.toString().includes(filterValue) ||
      payment.paid_at.toString().includes(filterValue)
    );
    setFilteredPayments(filtered);
  };

  // Open invoice modal and fetch templates
  const handleOpenInvoice = async (payment) => {
    setSelectedInvoice(payment);
    await fetchTemplates(payment.month); // Fetch templates when opening the invoice
    setOpenInvoice(true);
  };

  // Close invoice modal
  const handleCloseInvoice = () => {
    setOpenInvoice(false);
  };

  // Handle print functionality
  const handlePrint = () => {
    window.print();
  };

  useEffect(() => {
    const storedData = JSON.parse(localStorage.getItem('userData')); // Use 'userData' as key based on your login code
    if (storedData && storedData.fullname && storedData.id) {
      setFullname(storedData.fullname); // Set the full name from stored data
      setUserId(storedData.id); // Set the user ID from stored data
    }

    const fetchUserData = async () => {
      const uid = developeruid;

      try {
        const formData = new FormData();
        formData.append('uid', uid);

        const response = await fetch(apiConfig.getUrl(apiConfig.ENDPOINTS.DEV_GET_USER_DATA), {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          throw new Error('Failed to fetch user data');
        }

        const data = await response.json();
        console.log("userdata",data[0]);
        if (data.length > 0) {
          const account = data[0];
          setUsername(account.username);
          setFullname(account.fullname);
          setRole(account.role);
          setBankName(account.bank_name); // Set the bank_name
          setBankNo(account.bank_no); // Set the bank_no
          setAccountTitle(account.account_title); // Set the account_title
          setBalance(account.balance); // Set the balance
          console.log("balance:",balance)
          console.log("Account balance:", account.balance);
        }
      } catch (error) {
        console.error('Error fetching user data:', error.message);
      }
    };

    fetchUserData();
  }, []);

  return (
    <>
      {/* Header and TopHeader */}
      <div className='containers'>
        <Header />
        <div>
          <TopHeader title="Payments" />
          <div className="p-8">
            
            {/* Total Balance Section */}
            <Box className="bg-white p-6 rounded-lg shadow-md mb-8">
              <Typography variant="h5" fontWeight="bold">
                Total Balance
              </Typography>
              <Typography variant="h3" fontWeight="bold" color="primary" className="my-2">
                SAR {(balance!== null)?(
                  <>
                  {balance}
                  </>
                ):(0)}
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                {firstPaymentDate && lastPaymentDate
                  ? `From ${formatDate(firstPaymentDate)} To ${formatDate(lastPaymentDate)}`
                  : 'No payments available'}
              </Typography>
            </Box>

            {/* Invoices Section */}
            <TextField
              label="Search Payments"
              fullWidth
              value={filter}
              onChange={(e) => filterPayments(e.target.value)}
              sx={{ my: 2 }}
              variant="outlined"
              className="shadow-md"
            />
            <TableContainer component={Paper} className="shadow-md rounded-lg">
              <Table>
              <TableHead className="bg-gray-50">
  <TableRow>
    <TableCell className="font-semibold">{isLang === 'eng' ? 'ID' : 'معرف'}</TableCell>
    <TableCell className="font-semibold">{isLang === 'eng' ? 'Month' : 'الشهر'}</TableCell>
    <TableCell className="font-semibold">{isLang === 'eng' ? 'Total Amount' : 'المبلغ الإجمالي'}</TableCell>
    <TableCell className="font-semibold">{isLang === 'eng' ? 'Paid At' : 'تم الدفع في'}</TableCell>
    <TableCell className="font-semibold">{isLang === 'eng' ? 'Actions' : 'الإجراءات'}</TableCell>
  </TableRow>
</TableHead>

                <TableBody>
                  {filteredPayments.length > 0 ? (
                    filteredPayments.map((payment, index) => (
                      <TableRow key={index}>
                        <TableCell>INV00{index + 1}</TableCell>
                        <TableCell>{getMonthName(payment.month)}</TableCell> {/* Convert month number to name */}
                        <TableCell>SAR {parseFloat(payment.total_amount).toLocaleString()}</TableCell>
                        <TableCell>{payment.paid_at}</TableCell>
                        <TableCell>
                          <AiOutlineEye
                            className="text-purple-600 cursor-pointer"
                            onClick={() => handleOpenInvoice(payment)}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center">
                        No payments found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Invoice Modal */}
            <Dialog open={openInvoice} onClose={handleCloseInvoice} fullWidth maxWidth="md">
  
  <DialogContent>
    {selectedInvoice && (
      <div id="invoice">
        <Box p={4} className="bg-white shadow-md rounded-lg">
          {/* Invoice Header */}
          <Box display="flex" justifyContent="space-between">
            <img src={`${apiConfig.API_URL}/images/logo.png`} className='w-52'></img>
            <span className='text-4xl font-light text-right'>INVOICE<br/> <span className='text-xl text-gray-400'> #{selectedInvoice.id ? `00${selectedInvoice.id}` : '001'}</span></span>
          </Box>

          <Box display="flex" justifyContent="space-between" mt={2}>
            <Box>
              <Typography variant="subtitle1">{fullname || 'Admin'}</Typography>
              <Typography variant="subtitle1" style={{marginTop:'40px'}}>Bill to
                <br/>
                <span style={{fontWeight:'bold'
                }}>Admin</span></Typography>
            </Box>

         

            {/* Details Table on the Right */}
            <Box>
              <TableContainer component={Paper} className="details-table">
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell align="right">Date</TableCell>
                      <TableCell align="right">
                        {selectedInvoice.paid_at ? selectedInvoice.paid_at.split(' ')[0] : 'Sept 17, 2024'}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="right">Payment Terms</TableCell>
                      <TableCell align="right">Must be paid online</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="right">Due Date</TableCell>
                      <TableCell align="right">{selectedInvoice.due_date || 'Sept 19, 2024'}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell align="right" style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold', color: 'black' }}>
                        Balance Due
                      </TableCell>
                      <TableCell align="right" style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold', color: 'black' }}>
                        {balance !== null ? `$${balance}` : '$0.00'}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Box>

          {/* Invoice Table */}
          <TableContainer component={Paper} className="mt-4">
            <Table>
            <TableHead>
  <TableRow style={{ backgroundColor: '#393939', fontWeight: 'bold' }} >
    <TableCell style={{color:'white'}}>Item</TableCell>
    <TableCell style={{color:'white'}}>Quantity</TableCell>
    <TableCell style={{color:'white'}}>Rate</TableCell>
    <TableCell style={{color:'white'}}>Amount</TableCell>
  </TableRow>
</TableHead>

              <TableBody>
                {templates.length > 0 ? (
                  templates.map((template, index) => (
                    <TableRow key={index}>
                      <TableCell>{template.title}</TableCell>
                      <TableCell>1</TableCell>
                      <TableCell>
                        ${parseFloat(template.amount).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        ${parseFloat(template.amount).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center">
                      No items found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box>
  <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
    <Table sx={{ borderCollapse: 'collapse' }}>
      <TableBody>
        <TableRow sx={{ borderBottom: 'none' }}>
          <TableCell align="right" sx={{ borderBottom: 'none' , fontWeight: 'bold'}}>Total</TableCell>
          <TableCell align="right" sx={{ borderBottom: 'none', fontWeight: 'bold' }}>
            ${(selectedInvoice.total_amount || 0).toLocaleString()}
          </TableCell>
        </TableRow>
        <TableRow sx={{ borderBottom: 'none' }}>
          <TableCell align="right" sx={{ borderBottom: 'none' , fontWeight: 'bold' }}>Amount Paid</TableCell>
          <TableCell align="right" sx={{ borderBottom: 'none' , fontWeight: 'bold'}}>
            ${(selectedInvoice.total_amount || 0).toLocaleString()}
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  </TableContainer>
</Box>



          {/* Notes */}
          <Typography variant="subtitle2" className="mt-4 text-gray-500">
  Notes: 
</Typography>
<Typography variant="subtitle2" className="mt-4">
  Thank you for your payment.
</Typography>

{/* Payment Details */}
<Typography variant="subtitle2" className="mt-4 text-gray-500">
  Payment Details:
</Typography>
<Typography variant="body2" sx={{ lineHeight: 1.8 }}> {/* Adjust lineHeight for spacing */}
  Account Holder Name: {accountTitle || 'John Doe'}
  <br />
  Bank Name: {bankName || 'National Bank'}
  <br />
  Account Number: {bankNo || '123456789012'}
  <br />
  SWIFT/BIC: GB82WEST12345698765432
  <br />
  Branch Address: 123 Main St, London, UK
  <br />
  Routing Number: 110000000
</Typography>

        </Box>
      </div>
    )}
  </DialogContent>
  <DialogActions>
    <Button onClick={handlePrint} variant="contained" color="primary">
      Print
    </Button>
    <Button onClick={handleCloseInvoice} variant="outlined">
      Close
    </Button>
  </DialogActions>
</Dialog>


          </div>
        </div>
      </div>
    </>
  );
};

export default Payments;
